import { getSchoolTheme } from './get-school-theme';

/**
 * Per-field wins over the taxonomy-derived school theme when explicitly
 * passed. `iconPath: null` hides the school badge entirely.
 */
export type CatalogCardThemeOverride = {
  accentColor?: string;
  titleColor?: string;
  iconPath?: string | null;
};

export type CatalogCardTheme = {
  accentColor: string;
  titleColor: string;
  textColor: string;
  backgroundColor: string;
  borderColor: string;
  iconPath: string | null;
};

/**
 * Card theme resolution for `CatalogCard`. Masters degrees keep their gold
 * treatment regardless of the override; otherwise the taxonomy-derived school
 * theme is the base and each override field that is explicitly passed wins.
 * School landing pages use the override to badge cards with the school being
 * browsed, which is not always the school the catalog taxonomy assigns.
 */
export function resolveCatalogCardTheme({
  schoolUri,
  isMastersDegree = false,
  isMBA = false,
  themeOverride,
}: {
  schoolUri?: string;
  isMastersDegree?: boolean;
  isMBA?: boolean;
  themeOverride?: CatalogCardThemeOverride;
}): CatalogCardTheme {
  if (isMBA) {
    return {
      accentColor: 'gold.300',
      titleColor: 'gold.300',
      textColor: 'crimson.100',
      backgroundColor: 'crimson.900',
      borderColor: 'crimson.900',
      iconPath: '/images/school-icons/masters.svg',
    };
  }

  if (isMastersDegree) {
    return {
      accentColor: 'gold.300',
      titleColor: 'gold.300',
      textColor: 'blue.100',
      backgroundColor: 'blue.900',
      borderColor: 'blue.900',
      iconPath: '/images/school-icons/masters.svg',
    };
  }

  const schoolTheme = getSchoolTheme(schoolUri);

  return {
    accentColor: themeOverride?.accentColor ?? schoolTheme.accentColor,
    titleColor: themeOverride?.titleColor ?? schoolTheme.titleColor,
    textColor: 'grey.500',
    backgroundColor: 'grey.100',
    borderColor: 'grey.100',
    iconPath: themeOverride?.iconPath !== undefined ? themeOverride.iconPath : schoolTheme.iconPath,
  };
}
